//client/src/pages/admin/CtaResults.js
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Pagination from '../../components/ui/Pagination.js';

function StatCard({ label, value, subtext }) {
  return (
    <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-5 text-center">
      <p className="text-4xl font-bold text-gold">{value}</p>
      <p className="text-base text-royal-200 mt-1">{label}</p>
      {subtext && <p className="text-sm text-royal-200 mt-1">{subtext}</p>}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDays(days) {
  if (days === null || days === undefined) return '—';
  const rounded = Math.round(days * 10) / 10;
  if (rounded < 1) {
    const hours = Math.round(days * 24);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  return `${rounded} day${rounded !== 1 ? 's' : ''}`;
}

export default function CtaResults() {
  const [data, setData] = useState({ conversions: [], total: 0, page: 1, totalPages: 1, stats: {} });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      const result = await adminService.getCtaConversions(params.toString());
      setData(result);
    } catch (err) {
      console.error('Fetch CTA conversions error:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && data.conversions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-gold">CTA Conversion Results</h1>
          <p className="text-royal-200 text-base mt-1">
            Users who received an eCard, clicked "Create Your Own", then signed up
          </p>
        </div>
        <Link
          to="/admin/analytics"
          className="px-4 py-2 text-royal-200 border border-royal-600 rounded-lg hover:text-white hover:border-royal-200 transition-colors"
        >
          ← Back to Analytics
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <StatCard 
          label="Total Conversions" 
          value={data.stats?.totalConversions || 0} 
          subtext="Users acquired via eCards"
        />
        <StatCard 
          label="Avg. Time to Convert" 
          value={formatDays(data.stats?.avgDaysToConvert)} 
          subtext="From CTA click to signup"
        />
        <StatCard 
          label="Viral Growth" 
          value={data.stats?.totalConversions > 0 ? '✓ Active' : 'No data yet'} 
          subtext="Friend-to-friend spread"
        />
      </div>

      {/* Explanation Box */}
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-5 mb-6">
        <h2 className="text-white text-lg font-semibold mb-2">How This Works</h2>
        <p className="text-royal-200 text-base leading-relaxed">
          This page tracks <span className="text-gold">viral user acquisition</span>. When someone receives an eCard, 
          views it, and clicks the "Create Your Own eCard" button, we track that click. If they later create an account 
          using the same email address, they appear here. This shows how your platform grows organically through 
          friend-to-friend sharing.
        </p>
      </div>

      {/* Conversions Table */}
      <div className="bg-royal-800/40 border border-royal-600 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-royal-700 bg-royal-900/40">
                <th className="px-4 py-3 text-left text-royal-200 font-medium">New User</th>
                <th className="px-4 py-3 text-left text-royal-200 font-medium">Signed Up</th>
                <th className="px-4 py-3 text-left text-royal-200 font-medium">Card From</th>
                <th className="px-4 py-3 text-left text-royal-200 font-medium">Template</th>
                <th className="px-4 py-3 text-left text-royal-200 font-medium">CTA Clicked</th>
                <th className="px-4 py-3 text-right text-royal-200 font-medium">Time to Convert</th>
              </tr>
            </thead>
            <tbody>
              {data.conversions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <div className="text-royal-200">
                      <p className="text-lg mb-2">No conversions yet</p>
                      <p className="text-sm">
                        When recipients click "Create Your Own eCard" and later sign up, they'll appear here.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.conversions.map((c, idx) => (
                  <tr key={`${c.userid}-${idx}`} className="border-b border-royal-800 hover:bg-royal-800/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">
                          {c.firstname} {c.lastname}
                        </p>
                        <p className="text-royal-200 text-sm">{c.user_email}</p>
                        <p className="text-royal-200 text-sm">{c.typename}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-royal-100 whitespace-nowrap">
                      {formatDate(c.account_created)}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white">{c.sendername}</p>
                        <p className="text-royal-200 text-sm">
                          {c.sender_firstname} {c.sender_lastname}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-royal-100">
                      {c.templatename}
                    </td>
                    <td className="px-4 py-3 text-royal-100 whitespace-nowrap">
                      {formatDateTime(c.cta_clicked_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-gold font-semibold">
                        {formatDays(c.days_to_convert)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data.totalPages > 1 && (
        <div className="mt-4">
          <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
